import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import { collection, query, where, getDocs, doc, getDoc, setDoc, deleteDoc, onSnapshot } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { useModuleAdmin } from '../useModuleAdmin';
import { ownerStamp } from '../recordOwner';
import { ACTOR_BUCKET_FOR, normaliseRole, isAdminRole } from '../roles';
import { CalendarCheck, Calendar, Users, Plus, Trash2, Pencil, X, Settings as SettingsIcon } from 'lucide-react';
import HeaderActionsPortal from './HeaderActionsPortal';
import { useT } from '../i18n.jsx';
import '../theme-palette.css';

// Buckets a raw profile role into one of the roles the Settings "Module Admin" matrix is keyed on.
// Mirrors Invigilation.jsx/PatrolDuty.jsx's actorRoleBucket -- Admin is its own bucket (always granted
// via the isAdmin safety net in canManage), Student is its own non-grantable bucket so a "Staff" grant
// can never accidentally cover students.
// Maps a canonical role onto the permission-checkbox labels, which are stored in customer data and
// so are not migrated. Anything absent from the map -- parent, denied -- matches no permission list,
// which is exactly the intent: they hold no duty permissions of any kind.
const actorRoleBucket = (rawRole) => ACTOR_BUCKET_FOR[normaliseRole(rawRole)] || 'Denied';

// Class filter + search + "Select All (Filtered)" + bordered scrollable checklist, modeled directly on
// OLE Management's "Manage Participants" picker for a familiar pattern across modules. `groups` (when
// supplied) adds an "Add by Group" shortcut on top of that; `allowSelectAll=false` hides the bulk
// Select-All row -- used for the Group definition form itself, where a group is meant to be a
// hand-picked list of individuals, not just "select all of a class" (which would just duplicate the
// class list and defeat the point of saving it as a group). `clashingSids` is a Set of sids to flag red
// -- a per-day-threshold warning (Settings-configured, Quiz and Tutorial each have their own) that's
// purely visual unless that threshold is set to "reject" mode, which is enforced separately at save time.
const StudentPicker = ({ studentList, classOptions, groups, selected, onChange, allowSelectAll = true, clashingSids = new Set() }) => {
  const { t } = useT();
  const [search, setSearch] = useState('');
  const [classFilter, setClassFilter] = useState('');
  const [groupToAdd, setGroupToAdd] = useState('');
  const [showSelectedModal, setShowSelectedModal] = useState(false);
  const selectAllRef = useRef(null);

  const filtered = useMemo(() => {
    const txt = search.trim().toLowerCase();
    return studentList.filter(s => {
      const matchCls = !classFilter || s.currentClass === classFilter;
      const matchTxt = !txt || s.name.toLowerCase().includes(txt) || (s.chineseName || '').includes(search) || s.sid.toLowerCase().includes(txt);
      return matchCls && matchTxt;
    });
  }, [studentList, search, classFilter]);

  const toggle = (sid) => { onChange(selected.includes(sid) ? selected.filter(s => s !== sid) : [...selected, sid]); };

  const allFilteredSelected = filtered.length > 0 && filtered.every(s => selected.includes(s.sid));
  useEffect(() => {
    if (!selectAllRef.current) return;
    const selectedCount = filtered.filter(s => selected.includes(s.sid)).length;
    selectAllRef.current.indeterminate = selectedCount > 0 && selectedCount < filtered.length;
  }, [filtered, selected]);

  const toggleSelectAllFiltered = () => {
    if (allFilteredSelected) {
      const filteredSids = new Set(filtered.map(s => s.sid));
      onChange(selected.filter(sid => !filteredSids.has(sid)));
    } else {
      const merged = new Set(selected);
      filtered.forEach(s => merged.add(s.sid));
      onChange([...merged]);
    }
  };

  const addByGroup = () => {
    if (!groupToAdd) return;
    const group = groups.find(g => g.id === groupToAdd);
    if (!group) return;
    onChange([...new Set([...selected, ...(group.students || [])])]);
    setGroupToAdd('');
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.6rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <button
            type="button" onClick={() => setShowSelectedModal(true)} disabled={selected.length === 0}
            style={{
              background: 'none', border: 'none', padding: 0, fontSize: '0.8rem',
              cursor: selected.length === 0 ? 'default' : 'pointer',
              color: selected.length === 0 ? 'var(--color-text-muted)' : 'var(--color-primary)',
              textDecoration: selected.length === 0 ? 'none' : 'underline',
            }}
          >
            {selected.length} student(s) selected
          </button>
          {clashingSids.size > 0 && selected.some(sid => clashingSids.has(sid)) && (
            <span style={{ fontSize: '0.75rem', color: 'var(--color-danger)', fontWeight: '600' }}>
              {selected.filter(sid => clashingSids.has(sid)).length} over today's limit
            </span>
          )}
        </div>
        {groups && (
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <select className="input-field" value={groupToAdd} onChange={e => setGroupToAdd(e.target.value)} style={{ width: '160px' }}>
              <option value="">{t('quizTutorial.byGroup')}</option>
              {groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
            </select>
            <button type="button" className="btn-outline" onClick={addByGroup} style={{ width: 'auto', padding: '0.35rem 0.75rem', fontSize: '0.8rem' }}>{t('common.add')}</button>
          </div>
        )}
      </div>
      <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '0.6rem' }}>
        <select className="input-field" value={classFilter} onChange={e => setClassFilter(e.target.value)} style={{ width: '140px', flexShrink: 0 }}>
          <option value="">{t('common.allClasses')}</option>
          {classOptions.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <input
          type="text" className="input-field" placeholder={t('quizTutorial.searchStudents')}
          value={search} onChange={e => setSearch(e.target.value)} style={{ flex: 1 }}
        />
      </div>
      {allowSelectAll && (
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem', marginBottom: '0.5rem', cursor: filtered.length === 0 ? 'default' : 'pointer', color: filtered.length === 0 ? 'var(--color-text-muted)' : 'inherit' }}>
          <input
            ref={selectAllRef} type="checkbox" checked={allFilteredSelected} onChange={toggleSelectAllFiltered}
            disabled={filtered.length === 0} style={{ width: '15px', height: '15px', cursor: 'pointer' }}
          />
          {classFilter
            ? t('quizTutorial.selectAllInClass', { className: classFilter, count: filtered.length })
            : t('quizTutorial.selectAllFiltered', { count: filtered.length })}
        </label>
      )}
      <div style={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', maxHeight: '300px', overflowY: 'auto' }}>
        {filtered.length === 0 ? (
          <div style={{ padding: '1.5rem', textAlign: 'center', color: 'var(--color-text-muted)', fontSize: '0.875rem' }}>{t('quizTutorial.noMatchingStudents')}</div>
        ) : filtered.map(s => {
          const clashing = clashingSids.has(s.sid);
          return (
            <label
              key={s.sid}
              style={{
                display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.5rem 0.75rem',
                borderBottom: '1px solid var(--color-border)', fontSize: '0.875rem', cursor: 'pointer',
                backgroundColor: clashing ? 'var(--color-danger-bg)' : 'transparent',
              }}
            >
              <input
                type="checkbox" checked={selected.includes(s.sid)} onChange={() => toggle(s.sid)}
                style={{ width: '15px', height: '15px', cursor: 'pointer', flexShrink: 0 }}
              />
              <span style={{ color: 'var(--color-text-muted)', width: '70px', flexShrink: 0 }}>{s.currentClass} ({s.classNumber})</span>
              <span style={{ color: clashing ? 'var(--color-danger)' : 'inherit', fontWeight: clashing ? '600' : '400' }}>{s.name} {s.chineseName}</span>
              {clashing && <span style={{ marginLeft: 'auto', fontSize: '0.7rem', color: 'var(--color-danger)', fontWeight: '600', flexShrink: 0 }}>{t('quizTutorial.overDailyLimit')}</span>}
            </label>
          );
        })}
      </div>

      {showSelectedModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'var(--color-overlay)', zIndex: 120, display: 'flex', justifyContent: 'center', alignItems: 'flex-start', overflowY: 'auto', padding: '2rem' }}>
          <div style={{ backgroundColor: 'var(--color-bg-card)', padding: '2rem', borderRadius: 'var(--radius-lg)', width: '100%', maxWidth: '480px', margin: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3 style={{ fontSize: '1.1rem', fontWeight: '700', margin: 0 }}>{selected.length} student(s) selected</h3>
              <button type="button" onClick={() => setShowSelectedModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)', display: 'flex' }}>
                <X size={20} />
              </button>
            </div>
            <div style={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', maxHeight: '360px', overflowY: 'auto' }}>
              {selected.length === 0 ? (
                <div style={{ padding: '1.5rem', textAlign: 'center', color: 'var(--color-text-muted)', fontSize: '0.875rem' }}>{t('quizTutorial.noStudentsSelected')}</div>
              ) : selected.map(sid => {
                const st = studentList.find(s => s.sid === sid);
                const clashing = clashingSids.has(sid);
                return (
                  <div
                    key={sid}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.5rem 0.75rem',
                      borderBottom: '1px solid var(--color-border)', fontSize: '0.875rem',
                      backgroundColor: clashing ? 'var(--color-danger-bg)' : 'transparent',
                    }}
                  >
                    <span style={{ color: 'var(--color-text-muted)', width: '70px', flexShrink: 0 }}>{st ? `${st.currentClass} (${st.classNumber})` : ''}</span>
                    <span style={{ color: clashing ? 'var(--color-danger)' : 'inherit', fontWeight: clashing ? '600' : '400' }}>{st ? `${st.name} ${st.chineseName || ''}` : sid}</span>
                    {clashing && <span style={{ marginLeft: 'auto', fontSize: '0.7rem', color: 'var(--color-danger)', fontWeight: '600', flexShrink: 0 }}>{t('quizTutorial.overDailyLimit')}</span>}
                  </div>
                );
              })}
            </div>
            <button type="button" className="btn-outline" style={{ marginTop: '1rem' }} onClick={() => setShowSelectedModal(false)}>{t('common.close')}</button>
          </div>
        </div>
      )}
    </div>
  );
};

const emptyRecordForm = () => ({ date: '', subject: '', description: '', students: [] });
const emptyGroupForm = () => ({ name: '', students: [] });

// Every student who already appears in `max` or more OTHER same-day records (excluding the record
// currently being edited, if any) -- i.e. adding them to one more record that day would exceed the
// configured daily threshold. Used both to highlight risky students in the picker (regardless of
// whether they're selected yet) and, at save time, intersected with the actually-selected students to
// decide whether a "reject" threshold should block the save.
const computeExceededSids = (records, date, excludeId, max) => {
  const set = new Set();
  if (!date || !max) return set;
  const countBySid = {};
  records.forEach(r => {
    if (r.id === excludeId) return;
    if (r.date !== date) return;
    (r.students || []).forEach(sid => { countBySid[sid] = (countBySid[sid] || 0) + 1; });
  });
  Object.entries(countBySid).forEach(([sid, count]) => {
    if (count + 1 > max) set.add(sid);
  });
  return set;
};

const formatDateTime = (iso) => {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
};

const QuizTutorial = () => {
  const { t } = useT();
  const { activeSchoolId, userProfile, isMobile } = useOutletContext();
  const [activeTab, setActiveTab] = useState('quiz');

  const ownRole = (userProfile?.role || '').toLowerCase();
  const isAdmin = isAdminRole(ownRole);
  const ownEmail = (auth.currentUser?.email || '').toLowerCase();
  const ownActorRole = actorRoleBucket(ownRole);
  // Named admins of this module: the one list the rules read, replacing the per-action
  // staff pickers that used to live in this module's own settings document.
  const isModuleAdmin = useModuleAdmin(activeSchoolId, 'quiz_tutorial');

  const [academicYears, setAcademicYears] = useState([]);
  const [schoolYear, setSchoolYear] = useState('');
  const [subjects, setSubjects] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [studentList, setStudentList] = useState([]);

  useEffect(() => {
    if (!activeSchoolId) return;
    (async () => {
      try {
        const ySnap = await getDocs(collection(db, 'schools', activeSchoolId, 'academic_years'));
        const yList = [];
        ySnap.forEach(d => yList.push({ uid: d.id, ...d.data() }));
        yList.sort((a, b) => (a.yearId || '').localeCompare(b.yearId || ''));
        setAcademicYears(yList);
        if (yList.length > 0) {
          const cur = yList.find(y => y.cur_year) || yList[yList.length - 1];
          setSchoolYear(cur.yearId);
        }
      } catch (e) { console.error('Error loading academic years', e); }

      try {
        const sysSnap = await getDoc(doc(db, 'schools', activeSchoolId, 'metadata', 'system_settings'));
        const dropdowns = sysSnap.exists() ? (sysSnap.data().dropdowns || {}) : {};
        setSubjects((dropdowns.subjects || []).filter(s => s.active !== false));
      } catch (e) { console.error('Error loading system settings', e); }

      try {
        const staffSnap = await getDocs(collection(db, 'schools', activeSchoolId, 'staff_profiles'));
        const list = [];
        staffSnap.forEach(d => {
          const data = d.data();
          if (!data.initial) return;
          list.push({ email: d.id, initial: data.initial, name: `${data.lastName || ''} ${data.firstName || ''}`.trim() || data.initial });
        });
        list.sort((a, b) => a.initial.localeCompare(b.initial));
        setStaffList(list);
      } catch (e) { console.error('Error loading staff list', e); }

      try {
        const studentSnap = await getDocs(collection(db, 'schools', activeSchoolId, 'student_profiles'));
        const stList = [];
        studentSnap.forEach(d => {
          const data = d.data();
          if (!data.sid) return;
          stList.push({
            sid: data.sid,
            // Records are keyed by email -- the only identifier a security rule can verify against
            // the caller. The sid and class fields below stay for display, never for identity.
            email: (data.email || d.id || '').toLowerCase(),
            guardianEmails: [data.guardianEmail, data.guardianEmail2]
              .filter(Boolean)
              .map(e => String(e).toLowerCase()),
            name: `${data.lastName || ''} ${data.firstName || ''}`.trim() || data.name || 'Unknown',
            chineseName: data.chineseName || '',
            currentClass: data.currentClass || '',
            classNumber: data.classNumber || '',
          });
        });
        stList.sort((a, b) => (a.currentClass || '').localeCompare(b.currentClass || '', undefined, { numeric: true }) || Number(a.classNumber || 0) - Number(b.classNumber || 0));
        setStudentList(stList);
      } catch (e) { console.error('Error loading student list', e); }
    })();
  }, [activeSchoolId]);

  const staffByEmail = useMemo(() => Object.fromEntries(staffList.map(s => [s.email, s])), [staffList]);
  const studentBySid = useMemo(() => Object.fromEntries(studentList.map(s => [s.sid, s])), [studentList]);

  // See OLEManagement: records are keyed by email because that is the only identifier a rule can
  // verify; the SID list stays because every filter and column in this module reads it.
  const emailsForSids = (sids) => {
    const students = new Set();
    const guardians = new Set();
    (sids || []).forEach(sid => {
      const st = studentBySid[sid];
      if (!st) return;
      if (st.email) students.add(st.email);
      (st.guardianEmails || []).forEach(g => guardians.add(g));
    });
    return { studentEmails: [...students], guardianEmails: [...guardians] };
  };

  const classOptions = useMemo(() => [...new Set(studentList.map(s => s.currentClass))].filter(Boolean).sort((a, b) => a.localeCompare(b, undefined, { numeric: true })), [studentList]);

  // --- Settings: Module Admin (who can edit thresholds/permissions) + per-day threshold for how many
  // quizzes/tutorials a single student can be booked into before a warning (or an outright reject) kicks
  // in. Threshold edits are buffered as a draft and only take effect on Save -- same reasoning as
  // Invigilation's examPeriods draft -- so mid-edit typing in Settings doesn't change what validation
  // someone else is seeing on the Add Quiz/Tutorial form right now.
  const [editPermissions, setEditPermissions] = useState({ roles: [] });
  const [quizThreshold, setQuizThreshold] = useState({ max: 2, mode: 'warn' });
  const [quizThresholdDraft, setQuizThresholdDraft] = useState({ max: 2, mode: 'warn' });
  const [tutorialThreshold, setTutorialThreshold] = useState({ max: 1, mode: 'warn' });
  const [tutorialThresholdDraft, setTutorialThresholdDraft] = useState({ max: 1, mode: 'warn' });
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);

  const canManage = isAdmin || isModuleAdmin || editPermissions.roles.includes(ownActorRole);

  useEffect(() => {
    if (!activeSchoolId) return;
    setSettingsLoaded(false);
    getDoc(doc(db, 'schools', activeSchoolId, 'metadata', 'quiz_tutorial_settings'))
      .then(snap => {
        const data = snap.exists() ? snap.data() : {};
        setEditPermissions({ roles: data.editPermissions?.roles || [] });
        const qt = { max: Number(data.quizThreshold?.max) || 2, mode: data.quizThreshold?.mode === 'reject' ? 'reject' : 'warn' };
        const tt = { max: Number(data.tutorialThreshold?.max) || 1, mode: data.tutorialThreshold?.mode === 'reject' ? 'reject' : 'warn' };
        setQuizThreshold(qt);
        setQuizThresholdDraft(qt);
        setTutorialThreshold(tt);
        setTutorialThresholdDraft(tt);
      })
      .catch(e => console.error('Error loading quiz/tutorial settings', e))
      .finally(() => setSettingsLoaded(true));
  }, [activeSchoolId]);

  const toggleEditRole = (role) => setEditPermissions(prev => ({
    ...prev, roles: prev.roles.includes(role) ? prev.roles.filter(r => r !== role) : [...prev.roles, role],
  }));

  const handleSaveSettings = async () => {
    setSavingSettings(true);
    try {
      const cleanQuiz = { max: Math.max(1, Number(quizThresholdDraft.max) || 1), mode: quizThresholdDraft.mode === 'reject' ? 'reject' : 'warn' };
      const cleanTutorial = { max: Math.max(1, Number(tutorialThresholdDraft.max) || 1), mode: tutorialThresholdDraft.mode === 'reject' ? 'reject' : 'warn' };
      await setDoc(doc(db, 'schools', activeSchoolId, 'metadata', 'quiz_tutorial_settings'), {
        editPermissions, quizThreshold: cleanQuiz, tutorialThreshold: cleanTutorial,
      }, { merge: true });
      setQuizThreshold(cleanQuiz);
      setQuizThresholdDraft(cleanQuiz);
      setTutorialThreshold(cleanTutorial);
      setTutorialThresholdDraft(cleanTutorial);
    } catch (e) {
      console.error('Error saving quiz/tutorial settings', e);
      alert(t('quizTutorial.saveSettingsFailed', { message: e.message }));
    }
    setSavingSettings(false);
  };

  // --- Groups: reusable named student lists, scoped to a school year like Quiz/Tutorial records ---
  const [groups, setGroups] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(false);
  useEffect(() => {
    if (!activeSchoolId || !schoolYear) { setGroups([]); return; }
    setLoadingGroups(true);
    const q = query(collection(db, 'schools', activeSchoolId, 'student_group'), where('schoolYear', '==', schoolYear));
    const unsub = onSnapshot(q, (snap) => {
      const list = [];
      snap.forEach(d => list.push({ id: d.id, ...d.data() }));
      list.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setGroups(list);
      setLoadingGroups(false);
    }, (e) => { console.error('Error loading groups', e); setLoadingGroups(false); });
    return unsub;
  }, [activeSchoolId, schoolYear]);

  // --- Quiz / Tutorial records ---
  const [quizRecords, setQuizRecords] = useState([]);
  const [loadingQuiz, setLoadingQuiz] = useState(false);
  useEffect(() => {
    if (!activeSchoolId || !schoolYear) { setQuizRecords([]); return; }
    setLoadingQuiz(true);
    const q = query(collection(db, 'schools', activeSchoolId, 'quiz_record'), where('schoolYear', '==', schoolYear));
    const unsub = onSnapshot(q, (snap) => {
      const list = [];
      snap.forEach(d => list.push({ id: d.id, ...d.data() }));
      list.sort((a, b) => (b.date || '').localeCompare(a.date || ''));
      setQuizRecords(list);
      setLoadingQuiz(false);
    }, (e) => { console.error('Error loading quiz records', e); setLoadingQuiz(false); });
    return unsub;
  }, [activeSchoolId, schoolYear]);

  const [tutorialRecords, setTutorialRecords] = useState([]);
  const [loadingTutorial, setLoadingTutorial] = useState(false);
  useEffect(() => {
    if (!activeSchoolId || !schoolYear) { setTutorialRecords([]); return; }
    setLoadingTutorial(true);
    const q = query(collection(db, 'schools', activeSchoolId, 'tutorial_record'), where('schoolYear', '==', schoolYear));
    const unsub = onSnapshot(q, (snap) => {
      const list = [];
      snap.forEach(d => list.push({ id: d.id, ...d.data() }));
      list.sort((a, b) => (b.date || '').localeCompare(a.date || ''));
      setTutorialRecords(list);
      setLoadingTutorial(false);
    }, (e) => { console.error('Error loading tutorial records', e); setLoadingTutorial(false); });
    return unsub;
  }, [activeSchoolId, schoolYear]);

  // --- Add/Edit Quiz or Tutorial record modal (shared -- the two are identical in shape) ---
  const [showRecordModal, setShowRecordModal] = useState(false);
  const [recordModalType, setRecordModalType] = useState('quiz'); // 'quiz' | 'tutorial'
  const [editingRecordId, setEditingRecordId] = useState(null);
  const [recordForm, setRecordForm] = useState(emptyRecordForm());
  const [savingRecord, setSavingRecord] = useState(false);

  const openAddRecordModal = (type) => {
    setRecordModalType(type);
    setEditingRecordId(null);
    setRecordForm(emptyRecordForm());
    setShowRecordModal(true);
  };
  const openEditRecordModal = (type, record) => {
    setRecordModalType(type);
    setEditingRecordId(record.id);
    setRecordForm({ date: record.date || '', subject: record.subject || '', description: record.description || '', students: record.students || [] });
    setShowRecordModal(true);
  };

  // Records and the configured threshold for whichever type is currently open in the modal.
  const activeThresholdRecords = recordModalType === 'quiz' ? quizRecords : tutorialRecords;
  const activeThreshold = recordModalType === 'quiz' ? quizThreshold : tutorialThreshold;

  // Soft (or, in "reject" mode, hard) same-day threshold validation: every student who already appears
  // in `max` or more OTHER same-type records on the date currently in the form (excluding the record
  // being edited itself, so re-saving an existing record doesn't flag its own students against
  // themselves) -- flagged in the picker regardless of whether they're selected yet, so staff can see
  // who's already at the limit before adding them.
  const thresholdExceededSids = useMemo(
    () => computeExceededSids(activeThresholdRecords, recordForm.date, editingRecordId, activeThreshold.max),
    [activeThresholdRecords, recordForm.date, editingRecordId, activeThreshold.max]
  );

  const handleSaveRecordModal = async (e) => {
    e.preventDefault();
    if (!recordForm.date || !recordForm.subject) return;

    if (activeThreshold.mode === 'reject') {
      const violating = recordForm.students.filter(sid => thresholdExceededSids.has(sid));
      if (violating.length > 0) {
        const names = violating.map(sid => studentList.find(s => s.sid === sid)?.name || sid).join(', ');
        alert(t(recordModalType === 'quiz' ? 'quizTutorial.overLimitQuiz' : 'quizTutorial.overLimitTutorial', {
          count: violating.length, max: activeThreshold.max, names,
        }));
        return;
      }
    }

    setSavingRecord(true);
    try {
      const collectionName = recordModalType === 'quiz' ? 'quiz_record' : 'tutorial_record';
      const payload = {
        schoolYear, date: recordForm.date, subject: recordForm.subject,
        description: recordForm.description.trim(), students: recordForm.students,
        // Email lists alongside the SID list -- the SIDs drive display, these drive the rule.
        ...emailsForSids(recordForm.students),
      };
      if (editingRecordId) {
        await setDoc(doc(db, 'schools', activeSchoolId, collectionName, editingRecordId), payload, { merge: true });
      } else {
        payload.submittedBy = auth.currentUser?.email || '';
        payload.submittedAt = new Date().toISOString();
        await setDoc(doc(collection(db, 'schools', activeSchoolId, collectionName)), { ...payload, ...ownerStamp() });
      }
      setShowRecordModal(false);
    } catch (err) {
      console.error('Error saving record', err);
      alert(t('quizTutorial.saveFailed', { message: err.message }));
    }
    setSavingRecord(false);
  };

  const handleDeleteRecord = async (type, id) => {
    if (!window.confirm(t('quizTutorial.deleteRecordConfirm'))) return;
    try {
      await deleteDoc(doc(db, 'schools', activeSchoolId, type === 'quiz' ? 'quiz_record' : 'tutorial_record', id));
    } catch (err) {
      console.error('Error deleting record', err);
      alert(t('quizTutorial.deleteFailed', { message: err.message }));
    }
  };

  // --- Add/Edit Group modal ---
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [editingGroupId, setEditingGroupId] = useState(null);
  const [groupForm, setGroupForm] = useState(emptyGroupForm());
  const [savingGroup, setSavingGroup] = useState(false);

  const openAddGroupModal = () => { setEditingGroupId(null); setGroupForm(emptyGroupForm()); setShowGroupModal(true); };
  const openEditGroupModal = (g) => { setEditingGroupId(g.id); setGroupForm({ name: g.name || '', students: g.students || [] }); setShowGroupModal(true); };

  // View-only modal listing students, opened from a "N students" count button -- shared by the Groups
  // table's member count and the Quiz/Tutorial tables' Students column, so none of them need to spell
  // out names inline (which gets unreadable once a record has more than a couple students).
  const [viewingStudents, setViewingStudents] = useState(null); // { title, students } | null

  const handleSaveGroupModal = async (e) => {
    e.preventDefault();
    if (!groupForm.name.trim()) return;
    setSavingGroup(true);
    try {
      const payload = {
        schoolYear,
        name: groupForm.name.trim(),
        students: groupForm.students,
        ...emailsForSids(groupForm.students),
      };
      if (editingGroupId) {
        await setDoc(doc(db, 'schools', activeSchoolId, 'student_group', editingGroupId), payload, { merge: true });
      } else {
        payload.submittedBy = auth.currentUser?.email || '';
        payload.submittedAt = new Date().toISOString();
        await setDoc(doc(collection(db, 'schools', activeSchoolId, 'student_group')), { ...payload, ...ownerStamp() });
      }
      setShowGroupModal(false);
    } catch (err) {
      console.error('Error saving group', err);
      alert(t('quizTutorial.saveGroupFailed', { message: err.message }));
    }
    setSavingGroup(false);
  };

  const handleDeleteGroup = async (id) => {
    if (!window.confirm(t('quizTutorial.deleteGroupConfirm'))) return;
    try {
      await deleteDoc(doc(db, 'schools', activeSchoolId, 'student_group', id));
    } catch (err) {
      console.error('Error deleting group', err);
      alert(t('quizTutorial.deleteFailed', { message: err.message }));
    }
  };

  const tabs = [
    { id: 'quiz', label: t('quizTutorial.tabs.quiz'), icon: CalendarCheck },
    { id: 'tutorial', label: t('quizTutorial.tabs.tutorial'), icon: Calendar },
    { id: 'groups', label: t('quizTutorial.tabs.groups'), icon: Users },
    { id: 'settings', label: t('quizTutorial.tabs.settings'), icon: SettingsIcon },
  ];

  const RecordsTable = ({ type, records, loading }) => (
    <div className="table-container" style={{ backgroundColor: 'var(--color-bg-card)', borderRadius: isMobile ? 0 : 'var(--radius-lg)', boxShadow: isMobile ? 'none' : 'var(--shadow-sm)', overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
        <thead>
          <tr style={{ backgroundColor: 'var(--color-bg-app)', borderBottom: '1px solid var(--color-border)', fontSize: '0.8rem', color: 'var(--color-text-muted)' }}>
            <th style={{ padding: '0.75rem 1rem', fontWeight: '600' }}>{t('common.date')}</th>
            <th style={{ padding: '0.75rem 1rem', fontWeight: '600' }}>{t('quizTutorial.subject')}</th>
            <th style={{ padding: '0.75rem 1rem', fontWeight: '600' }}>{t('quizTutorial.description')}</th>
            <th style={{ padding: '0.75rem 1rem', fontWeight: '600' }}>{t('common.students')}</th>
            <th style={{ padding: '0.75rem 1rem', fontWeight: '600' }}>{t('quizTutorial.submittedBy')}</th>
            <th style={{ padding: '0.75rem 1rem', fontWeight: '600' }}>{t('quizTutorial.submittedAt')}</th>
            <th style={{ padding: '0.75rem 1rem', fontWeight: '600' }}></th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr><td colSpan={7} style={{ textAlign: 'center', padding: '2rem', color: 'var(--color-text-muted)' }}>{t('common.loading')}</td></tr>
          ) : records.length === 0 ? (
            <tr><td colSpan={7} style={{ textAlign: 'center', padding: '2rem', color: 'var(--color-text-muted)' }}>{t(type === 'quiz' ? 'quizTutorial.noQuizzes' : 'quizTutorial.noTutorials', { year: schoolYear || t('quizTutorial.thisYear') })}</td></tr>
          ) : records.map((r, i) => (
            <tr key={r.id} style={{ borderBottom: i === records.length - 1 ? 'none' : '1px solid var(--color-border)', fontSize: '0.875rem' }}>
              <td style={{ padding: '0.75rem 1rem', fontWeight: '600', whiteSpace: 'nowrap' }}>{r.date}</td>
              <td style={{ padding: '0.75rem 1rem' }}>{r.subject}</td>
              <td style={{ padding: '0.75rem 1rem', color: 'var(--color-text-muted)', maxWidth: '280px' }}>{r.description || '-'}</td>
              <td style={{ padding: '0.75rem 1rem' }}>
                <button
                  type="button" onClick={() => setViewingStudents({ title: `${r.date} -- ${r.subject}`, students: r.students || [] })}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', padding: '0.2rem 0.7rem', borderRadius: '999px', border: '1px solid var(--color-border)', backgroundColor: 'var(--color-bg-app)', cursor: 'pointer', fontSize: '0.8rem', fontWeight: '600' }}
                >
                  <Users size={12} /> {(r.students || []).length} student{(r.students || []).length === 1 ? '' : 's'}
                </button>
              </td>
              <td style={{ padding: '0.75rem 1rem' }}>{staffByEmail[r.submittedBy]?.initial || r.submittedBy || '-'}</td>
              <td style={{ padding: '0.75rem 1rem', color: 'var(--color-text-muted)', whiteSpace: 'nowrap' }}>{formatDateTime(r.submittedAt)}</td>
              <td style={{ padding: '0.75rem 1rem', whiteSpace: 'nowrap' }}>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button type="button" onClick={() => openEditRecordModal(type, r)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)', display: 'flex' }}>
                    <Pencil size={16} />
                  </button>
                  <button type="button" onClick={() => handleDeleteRecord(type, r.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-danger)', display: 'flex' }}>
                    <Trash2 size={16} />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="animate-fade-in new-theme" style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Tabs Bar */}
      <div className="theme-tabs-bar" style={{ minHeight: '66px' }}>
        <div className="theme-tabs-group">
          {tabs.map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`theme-tab ${isActive ? 'active' : ''}`} style={{ whiteSpace: 'nowrap' }}>
                <div className="theme-tab-icon"><Icon size={18} /></div>
                {tab.label}
              </button>
            );
          })}
        </div>

        <HeaderActionsPortal>
        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
          {(activeTab === 'quiz' || activeTab === 'tutorial' || activeTab === 'groups') && (
            <select className="input-field" value={schoolYear} onChange={e => setSchoolYear(e.target.value)} style={{ width: '150px' }}>
              {academicYears.length === 0 ? <option value="">{t('quizTutorial.noYearsConfigured')}</option> : academicYears.map(y => <option key={y.uid} value={y.yearId}>{y.yearId}</option>)}
            </select>
          )}
          {activeTab === 'quiz' && (
            <button className="btn-primary" onClick={() => openAddRecordModal('quiz')} style={{ width: 'auto', display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.5rem 1rem' }}>
              <Plus size={16} /> {t('quizTutorial.addQuiz')}
            </button>
          )}
          {activeTab === 'tutorial' && (
            <button className="btn-primary" onClick={() => openAddRecordModal('tutorial')} style={{ width: 'auto', display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.5rem 1rem' }}>
              <Plus size={16} /> {t('quizTutorial.addTutorial')}
            </button>
          )}
          {activeTab === 'groups' && (
            <button className="btn-primary" onClick={openAddGroupModal} style={{ width: 'auto', display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.5rem 1rem' }}>
              <Plus size={16} /> {t('quizTutorial.addGroup')}
            </button>
          )}
          {activeTab === 'settings' && (
            <button className="btn-primary" onClick={handleSaveSettings} disabled={!canManage || savingSettings} style={{ width: 'auto', padding: '0.5rem 1.25rem' }}>
              {savingSettings ? t('common.saving') : t('common.save')}
            </button>
          )}
        </div>
        </HeaderActionsPortal>
      </div>

      {/* Main Content */}
      <div style={{ flex: 1, overflowY: 'auto' }}>
      <div style={{ padding: '0 1.5rem 1.5rem' }}>

        {activeTab === 'quiz' && <RecordsTable type="quiz" records={quizRecords} loading={loadingQuiz} />}
        {activeTab === 'tutorial' && <RecordsTable type="tutorial" records={tutorialRecords} loading={loadingTutorial} />}

        {activeTab === 'groups' && (
          <div className="table-container" style={{ backgroundColor: 'var(--color-bg-card)', borderRadius: isMobile ? 0 : 'var(--radius-lg)', boxShadow: isMobile ? 'none' : 'var(--shadow-sm)', overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
              <thead>
                <tr style={{ backgroundColor: 'var(--color-bg-app)', borderBottom: '1px solid var(--color-border)', fontSize: '0.8rem', color: 'var(--color-text-muted)' }}>
                  <th style={{ padding: '0.75rem 1rem', fontWeight: '600' }}>{t('quizTutorial.groupName')}</th>
                  <th style={{ padding: '0.75rem 1rem', fontWeight: '600' }}>{t('common.students')}</th>
                  <th style={{ padding: '0.75rem 1rem', fontWeight: '600' }}>{t('quizTutorial.submittedBy')}</th>
                  <th style={{ padding: '0.75rem 1rem', fontWeight: '600' }}>{t('quizTutorial.submittedAt')}</th>
                  <th style={{ padding: '0.75rem 1rem', fontWeight: '600' }}></th>
                </tr>
              </thead>
              <tbody>
                {loadingGroups ? (
                  <tr><td colSpan={5} style={{ textAlign: 'center', padding: '2rem', color: 'var(--color-text-muted)' }}>{t('common.loading')}</td></tr>
                ) : groups.length === 0 ? (
                  <tr><td colSpan={5} style={{ textAlign: 'center', padding: '2rem', color: 'var(--color-text-muted)' }}>No groups defined yet for {schoolYear || 'this year'}.</td></tr>
                ) : groups.map((g, i) => (
                  <tr key={g.id} style={{ borderBottom: i === groups.length - 1 ? 'none' : '1px solid var(--color-border)', fontSize: '0.875rem' }}>
                    <td style={{ padding: '0.75rem 1rem', fontWeight: '600' }}>{g.name}</td>
                    <td style={{ padding: '0.75rem 1rem' }}>
                      <button
                        type="button" onClick={() => setViewingStudents({ title: g.name, students: g.students || [] })}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', padding: '0.2rem 0.7rem', borderRadius: '999px', border: '1px solid var(--color-border)', backgroundColor: 'var(--color-bg-app)', cursor: 'pointer', fontSize: '0.8rem', fontWeight: '600' }}
                      >
                        <Users size={12} /> {(g.students || []).length} member{(g.students || []).length === 1 ? '' : 's'}
                      </button>
                    </td>
                    <td style={{ padding: '0.75rem 1rem' }}>{staffByEmail[g.submittedBy]?.initial || g.submittedBy || '-'}</td>
                    <td style={{ padding: '0.75rem 1rem', color: 'var(--color-text-muted)', whiteSpace: 'nowrap' }}>{formatDateTime(g.submittedAt)}</td>
                    <td style={{ padding: '0.75rem 1rem', whiteSpace: 'nowrap' }}>
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button type="button" onClick={() => openEditGroupModal(g)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)', display: 'flex' }}>
                          <Pencil size={16} />
                        </button>
                        <button type="button" onClick={() => handleDeleteGroup(g.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-danger)', display: 'flex' }}>
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === 'settings' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', width: '100%' }}>
            {!canManage && (
              <div style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', backgroundColor: 'var(--color-bg-card)', padding: '0.75rem 1rem', borderRadius: 'var(--radius-md)' }}>
                Only admins and assigned staff can change these settings. This is a read-only view.
              </div>
            )}

            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
              <div style={{ flex: '1 1 260px', minWidth: 0, backgroundColor: 'var(--color-bg-card)', padding: isMobile ? '0.75rem' : '1.5rem', borderRadius: isMobile ? 0 : 'var(--radius-lg)', boxShadow: isMobile ? 'none' : 'var(--shadow-sm)' }}>
                <h3 style={{ fontSize: '1.1rem', fontWeight: '700', margin: '0 0 0.4rem' }}>{t('quizTutorial.quizThreshold')}</h3>
                <p style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', margin: '0 0 1rem' }}>
                  How many quizzes a single student can be booked into on the same day before it's flagged.
                </p>
                <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', marginBottom: '0.4rem' }}>{t('quizTutorial.maxPerDay')}</label>
                    <input
                      type="number" min="1" className="input-field" disabled={!canManage}
                      value={quizThresholdDraft.max}
                      onChange={e => setQuizThresholdDraft(prev => ({ ...prev, max: e.target.value }))}
                      style={{ width: '100px' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', marginBottom: '0.4rem' }}>{t('quizTutorial.whenExceeded')}</label>
                    <select
                      className="input-field" disabled={!canManage} value={quizThresholdDraft.mode}
                      onChange={e => setQuizThresholdDraft(prev => ({ ...prev, mode: e.target.value }))}
                      style={{ width: '160px' }}
                    >
                      <option value="warn">{t('quizTutorial.warnOnly')}</option>
                      <option value="reject">{t('quizTutorial.rejectSave')}</option>
                    </select>
                  </div>
                </div>
              </div>

              <div style={{ flex: '1 1 260px', minWidth: 0, backgroundColor: 'var(--color-bg-card)', padding: isMobile ? '0.75rem' : '1.5rem', borderRadius: isMobile ? 0 : 'var(--radius-lg)', boxShadow: isMobile ? 'none' : 'var(--shadow-sm)' }}>
                <h3 style={{ fontSize: '1.1rem', fontWeight: '700', margin: '0 0 0.4rem' }}>{t('quizTutorial.tutorialThreshold')}</h3>
                <p style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', margin: '0 0 1rem' }}>
                  How many tutorials a single student can be booked into on the same day before it's flagged. Set to 1 if there's only one tutorial session per day.
                </p>
                <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', marginBottom: '0.4rem' }}>{t('quizTutorial.maxPerDay')}</label>
                    <input
                      type="number" min="1" className="input-field" disabled={!canManage}
                      value={tutorialThresholdDraft.max}
                      onChange={e => setTutorialThresholdDraft(prev => ({ ...prev, max: e.target.value }))}
                      style={{ width: '100px' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', marginBottom: '0.4rem' }}>{t('quizTutorial.whenExceeded')}</label>
                    <select
                      className="input-field" disabled={!canManage} value={tutorialThresholdDraft.mode}
                      onChange={e => setTutorialThresholdDraft(prev => ({ ...prev, mode: e.target.value }))}
                      style={{ width: '160px' }}
                    >
                      <option value="warn">{t('quizTutorial.warnOnly')}</option>
                      <option value="reject">{t('quizTutorial.rejectSave')}</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            <div style={{ backgroundColor: 'var(--color-bg-card)', padding: isMobile ? '0.75rem' : '1.5rem', borderRadius: isMobile ? 0 : 'var(--radius-lg)', boxShadow: isMobile ? 'none' : 'var(--shadow-sm)', maxWidth: '500px' }}>
              <h3 style={{ fontSize: '1.1rem', fontWeight: '700', margin: '0 0 1rem' }}>{t('quizTutorial.moduleAdmin')}</h3>
              <table style={{ borderCollapse: 'collapse', width: '100%', maxWidth: '320px' }}>
                <thead>
                  <tr style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', textAlign: 'left' }}>
                    <th style={{ padding: '0.5rem 0.75rem' }}>{t('modulePermissions.role')}</th>
                    <th style={{ padding: '0.5rem 0.75rem', textAlign: 'center' }}>{t('quizTutorial.canEditSettings')}</th>
                  </tr>
                </thead>
                <tbody>
                  {['Admin', 'Teacher', 'Staff'].map(role => (
                    <tr key={role} style={{ borderTop: '1px solid var(--color-border)' }}>
                      <td style={{ padding: '0.6rem 0.75rem', fontWeight: '600' }}>{t(`modulePermissions.buckets.${role.toLowerCase()}`, {}, role)}</td>
                      <td style={{ padding: '0.6rem 0.75rem', textAlign: 'center' }}>
                        <input
                          type="checkbox"
                          checked={role === 'Admin' || editPermissions.roles.includes(role)}
                          disabled={role === 'Admin' || !canManage}
                          onChange={() => toggleEditRole(role)}
                          style={{ width: '16px', height: '16px', cursor: role === 'Admin' ? 'default' : 'pointer' }}
                        />
                      </td>
                    </tr>
                  ))}
                  <tr style={{ borderTop: '1px solid var(--color-border)' }}>
                    <td style={{ padding: '0.6rem 0.75rem', fontWeight: '600' }}>{t('modulePermissions.others')}</td>
                    <td style={{ padding: '0.6rem 0.75rem', textAlign: 'center' }}>
                      <span style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)' }}>
                        {t('modulePermissions.namedInAccessControl')}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
      </div>

      {/* Manage "Others" -- Module Admin staff modal */}
      
      {/* Add / Edit Quiz or Tutorial Modal */}
      {showRecordModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'var(--color-overlay)', zIndex: 100, display: 'flex', justifyContent: 'center', alignItems: 'flex-start', overflowY: 'auto', padding: '2rem' }}>
          <div style={{ backgroundColor: 'var(--color-bg-card)', padding: '2rem', borderRadius: 'var(--radius-lg)', width: '100%', maxWidth: '700px', margin: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h3 style={{ fontSize: '1.25rem', fontWeight: '700', margin: 0 }}>
                {recordModalType === 'quiz'
                  ? t(editingRecordId ? 'quizTutorial.editQuiz' : 'quizTutorial.addQuiz')
                  : t(editingRecordId ? 'quizTutorial.editTutorial' : 'quizTutorial.addTutorial')}
              </h3>
              <button type="button" onClick={() => setShowRecordModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)', display: 'flex' }}>
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleSaveRecordModal} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('common.date')}</label>
                <input type="date" className="input-field" value={recordForm.date} onChange={e => setRecordForm(prev => ({ ...prev, date: e.target.value }))} required />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('quizTutorial.subject')}</label>
                <select className="input-field" value={recordForm.subject} onChange={e => setRecordForm(prev => ({ ...prev, subject: e.target.value }))} required>
                  <option value="">{t('quizTutorial.selectSubject')}</option>
                  {subjects.map(s => <option key={s.code} value={s.code}>{s.code}</option>)}
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('quizTutorial.description')}</label>
                <textarea className="input-field" rows={3} value={recordForm.description} onChange={e => setRecordForm(prev => ({ ...prev, description: e.target.value }))} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('quizTutorial.studentsInvolved')}</label>
                <StudentPicker
                  studentList={studentList} classOptions={classOptions} groups={groups} clashingSids={thresholdExceededSids}
                  selected={recordForm.students} onChange={(students) => setRecordForm(prev => ({ ...prev, students }))}
                />
              </div>
              <div style={{ display: 'flex', gap: '1rem', marginTop: '0.5rem' }}>
                <button type="button" className="btn-outline" style={{ flex: 1 }} onClick={() => setShowRecordModal(false)}>{t('common.cancel')}</button>
                <button type="submit" className="btn-primary" style={{ flex: 1 }} disabled={savingRecord}>{savingRecord ? t('common.saving') : t('common.save')}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add / Edit Group Modal */}
      {showGroupModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'var(--color-overlay)', zIndex: 100, display: 'flex', justifyContent: 'center', alignItems: 'flex-start', overflowY: 'auto', padding: '2rem' }}>
          <div style={{ backgroundColor: 'var(--color-bg-card)', padding: '2rem', borderRadius: 'var(--radius-lg)', width: '100%', maxWidth: '700px', margin: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h3 style={{ fontSize: '1.25rem', fontWeight: '700', margin: 0 }}>{t(editingGroupId ? 'quizTutorial.editGroup' : 'quizTutorial.addGroup')}</h3>
              <button type="button" onClick={() => setShowGroupModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)', display: 'flex' }}>
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleSaveGroupModal} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('quizTutorial.groupName')}</label>
                <input type="text" className="input-field" value={groupForm.name} onChange={e => setGroupForm(prev => ({ ...prev, name: e.target.value }))} required />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('quizTutorial.studentsInvolved')}</label>
                <StudentPicker
                  studentList={studentList} classOptions={classOptions} allowSelectAll={false}
                  selected={groupForm.students} onChange={(students) => setGroupForm(prev => ({ ...prev, students }))}
                />
              </div>
              <div style={{ display: 'flex', gap: '1rem', marginTop: '0.5rem' }}>
                <button type="button" className="btn-outline" style={{ flex: 1 }} onClick={() => setShowGroupModal(false)}>{t('common.cancel')}</button>
                <button type="submit" className="btn-primary" style={{ flex: 1 }} disabled={savingGroup}>{savingGroup ? t('common.saving') : t('common.save')}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Group Members Modal */}
      {viewingStudents && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'var(--color-overlay)', zIndex: 100, display: 'flex', justifyContent: 'center', alignItems: 'flex-start', overflowY: 'auto', padding: '2rem' }}>
          <div style={{ backgroundColor: 'var(--color-bg-card)', padding: '2rem', borderRadius: 'var(--radius-lg)', width: '100%', maxWidth: '480px', margin: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3 style={{ fontSize: '1.1rem', fontWeight: '700', margin: 0 }}>
                {viewingStudents.title} -- {viewingStudents.students.length} student{viewingStudents.students.length === 1 ? '' : 's'}
              </h3>
              <button type="button" onClick={() => setViewingStudents(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)', display: 'flex' }}>
                <X size={20} />
              </button>
            </div>
            <div style={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', maxHeight: '360px', overflowY: 'auto' }}>
              {viewingStudents.students.length === 0 ? (
                <div style={{ padding: '1.5rem', textAlign: 'center', color: 'var(--color-text-muted)', fontSize: '0.875rem' }}>{t('quizTutorial.noStudents')}</div>
              ) : viewingStudents.students.map(sid => {
                const st = studentList.find(s => s.sid === sid);
                return (
                  <div key={sid} style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.5rem 0.75rem', borderBottom: '1px solid var(--color-border)', fontSize: '0.875rem' }}>
                    <span style={{ color: 'var(--color-text-muted)', width: '70px', flexShrink: 0 }}>{st ? `${st.currentClass} (${st.classNumber})` : ''}</span>
                    <span>{st ? `${st.name} ${st.chineseName || ''}` : sid}</span>
                  </div>
                );
              })}
            </div>
            <button type="button" className="btn-outline" style={{ marginTop: '1rem' }} onClick={() => setViewingStudents(null)}>{t('common.close')}</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuizTutorial;
